import { useEffect, useMemo, useState } from "react";
import { Pin, Loader2, Check, AlertTriangle } from "lucide-react";
import api, { fmtINR, formatDetail } from "@/lib/api";
import Modal from "@/components/Modal";

/**
 * Pins a payment channel for a specific customer's NEXT (or every) deposit.
 * Calls the partner-scoped endpoint by default; pass `scope="admin"` to use
 * the admin endpoint (requires `partnerId`).
 */
export default function PinChannelButton({
    customerUsername,
    customerUserId,
    partnerId,
    scope = "partner",
    size = "sm",
    label = "Pin channel",
    onPinned,
}) {
    const [open, setOpen] = useState(false);
    const [channels, setChannels] = useState({ upi: [], bank: [] });
    const [pickedId, setPickedId] = useState("");
    const [mode, setMode] = useState("once");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [active, setActive] = useState(null); // existing override for this customer

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                if (scope === "partner") {
                    const r = await api.get("/partner/channels");
                    setChannels({ upi: r.data.upi_channels || [], bank: r.data.bank_accounts || [] });
                } else {
                    const r = await api.get(`/admin/partners/${partnerId}/channels`);
                    setChannels({ upi: r.data.upi_channels || [], bank: r.data.bank_accounts || [] });
                }
                const url = scope === "partner"
                    ? `/partner/customer-overrides?customer_user_id=${encodeURIComponent(customerUserId)}`
                    : `/admin/customer-overrides?partner_id=${encodeURIComponent(partnerId)}&customer_user_id=${encodeURIComponent(customerUserId)}`;
                const ar = await api.get(url);
                setActive(ar.data.items?.[0] || null);
            } catch {
                /* modal still renders */
            }
        })();
    }, [open, scope, partnerId, customerUserId]);

    // Resolve the picked channel + compute pin-time warnings client-side
    // (server returns these too, but inline preview is friendlier UX).
    const pickedChannel = useMemo(() => {
        if (!pickedId) return null;
        return [...channels.upi, ...channels.bank].find((c) => c.channel_id === pickedId) || null;
    }, [pickedId, channels]);

    const channelWarnings = useMemo(() => {
        if (!pickedChannel) return [];
        const w = [];
        if ((pickedChannel.daily_remaining ?? 0) <= 0) w.push({ key: "daily_amount", label: "Daily amount limit reached" });
        if ((pickedChannel.daily_txn_remaining ?? 0) <= 0) w.push({ key: "daily_txn", label: "Daily transaction count reached" });
        if (!pickedChannel.in_active_window) {
            const f = pickedChannel.active_from_hour, t = pickedChannel.active_to_hour;
            const win = (f != null && t != null) ? `${String(f).padStart(2, "0")}:00 – ${String(t).padStart(2, "0")}:00 IST` : "active window";
            w.push({ key: "outside_hours", label: `Outside active hours (${win})` });
        }
        return w;
    }, [pickedChannel]);

    const submit = async () => {
        if (!pickedId) return;
        setBusy(true);
        try {
            const body = {
                customer_user_id: customerUserId,
                customer_username: customerUsername,
                channel_id: pickedId,
                mode, note,
            };
            if (scope === "admin") body.partner_id = partnerId;
            const url = scope === "admin" ? "/admin/customer-overrides" : "/partner/customer-overrides";
            const r = await api.post(url, body);
            setOpen(false);
            setPickedId(""); setNote(""); setMode("once");
            onPinned?.(r.data.override);
        } catch (e) {
            alert(formatDetail(e?.response?.data?.detail) || "Failed");
        } finally { setBusy(false); }
    };

    const removeActive = async () => {
        if (!active) return;
        if (!window.confirm("Remove the existing pin for this customer?")) return;
        setBusy(true);
        try {
            const url = scope === "admin"
                ? `/admin/customer-overrides/${active.id}`
                : `/partner/customer-overrides/${active.id}`;
            await api.delete(url);
            setActive(null);
        } finally { setBusy(false); }
    };

    return (
        <>
            <button
                data-testid={`pin-channel-${customerUserId}`}
                onClick={() => setOpen(true)}
                title="Pin a payment channel for this customer's next deposit"
                className={`btn-secondary border-blue-200 text-blue-700 hover:bg-blue-50 rounded-md text-xs flex items-center gap-1 ${size === "sm" ? "px-2.5 py-1.5" : "px-3 py-2"}`}
            >
                <Pin className="w-3.5 h-3.5" /> {label}
            </button>
            {open && (
                <Modal testId="pin-channel-modal" onClose={() => setOpen(false)}
                    title={`Pin channel for ${customerUsername || customerUserId}`}>
                    <div className="space-y-4">
                        {active && (
                            <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm">
                                <div className="font-medium text-blue-900 flex items-center gap-1.5">
                                    <Pin className="w-3.5 h-3.5" /> Currently pinned: {active.channel_label || active.channel_id}
                                </div>
                                <div className="text-[11px] text-blue-700 mt-0.5">Mode: <strong>{active.mode}</strong>{active.note && ` · ${active.note}`}</div>
                                <button onClick={removeActive} disabled={busy} className="mt-2 text-rose-700 underline text-xs">Remove this pin</button>
                            </div>
                        )}
                        <div>
                            <label className="label">Channel</label>
                            <select data-testid="pin-channel-select"
                                className="input"
                                value={pickedId}
                                onChange={(e) => setPickedId(e.target.value)}>
                                <option value="">— select a channel —</option>
                                {channels.upi.map((c) => <option key={c.channel_id} value={c.channel_id}>UPI · {c.label}</option>)}
                                {channels.bank.map((c) => <option key={c.channel_id} value={c.channel_id}>BANK · {c.label}</option>)}
                            </select>
                            {pickedChannel && (
                                <div data-testid="pin-channel-state" className="mt-2 rounded-md bg-slate-50 border border-slate-200 p-2.5 text-[11px] text-slate-600 space-y-1">
                                    <div className="flex items-center justify-between">
                                        <span>Daily amount remaining</span>
                                        <span className={`font-mono font-medium ${(pickedChannel.daily_remaining ?? 0) <= 0 ? "text-rose-600" : "text-slate-900"}`}>
                                            {fmtINR(pickedChannel.daily_remaining ?? 0)}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span>Daily txns remaining</span>
                                        <span className={`font-mono font-medium ${(pickedChannel.daily_txn_remaining ?? 0) <= 0 ? "text-rose-600" : "text-slate-900"}`}>
                                            {pickedChannel.daily_txn_remaining ?? 0}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span>In active window</span>
                                        <span className={`font-medium ${pickedChannel.in_active_window ? "text-emerald-700" : "text-amber-700"}`}>
                                            {pickedChannel.in_active_window ? "Yes" : "No"}
                                        </span>
                                    </div>
                                </div>
                            )}
                            {channelWarnings.length > 0 && (
                                <div data-testid="pin-channel-warning" className="mt-2 rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900">
                                    <div className="flex items-start gap-1.5">
                                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <div className="font-medium">Heads up — this pin may not apply right now:</div>
                                            <ul className="mt-1 list-disc list-inside space-y-0.5">
                                                {channelWarnings.map((w) => <li key={w.key}>{w.label}</li>)}
                                            </ul>
                                            <div className="mt-1.5 text-[11px] text-amber-800">
                                                Smart routing will silently fall back to another eligible channel until the limit resets / window opens.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="label">Apply for</label>
                            <div className="flex gap-2">
                                <button data-testid="pin-mode-once" onClick={() => setMode("once")}
                                    className={`flex-1 px-3 py-2 rounded-md text-sm border-2 transition-colors ${mode === "once" ? "border-blue-500 bg-blue-50 text-blue-900" : "border-slate-200 bg-white text-slate-700"}`}>
                                    <div className="font-medium">Just next payment</div>
                                    <div className="text-[11px] text-slate-500 mt-0.5">Override clears after first use</div>
                                </button>
                                <button data-testid="pin-mode-always" onClick={() => setMode("always")}
                                    className={`flex-1 px-3 py-2 rounded-md text-sm border-2 transition-colors ${mode === "always" ? "border-blue-500 bg-blue-50 text-blue-900" : "border-slate-200 bg-white text-slate-700"}`}>
                                    <div className="font-medium">Every future payment</div>
                                    <div className="text-[11px] text-slate-500 mt-0.5">Sticky — VIP customers</div>
                                </button>
                            </div>
                        </div>
                        <div>
                            <label className="label">Note (optional)</label>
                            <input data-testid="pin-note" value={note} onChange={(e) => setNote(e.target.value)}
                                placeholder="Why are you pinning this?" className="input" />
                        </div>
                        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                            <button onClick={() => setOpen(false)} className="btn-secondary px-4 py-2 rounded-md text-sm">Cancel</button>
                            <button data-testid="pin-channel-submit" onClick={submit} disabled={!pickedId || busy} className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-40">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Pin channel
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
}
